import React from 'react';
import '../publishing-dialog.css';
import { useParams } from 'react-router-dom';
import Button from '../../../lib/button/Button';
import PublicationHistoryContent from './PublicationHistoryContent';
import { EditorService } from '../../../services/editor';
import DialogLoader from '../../DialogLoader';
import {
	AWS_SERVER_SIDE_PROJECT_DEPLOYMENT_SEQUENCE,
	InfrastructureResourceType,
} from '@elyx-code/definitions';
import { ProjectState } from '@elyx-code/project-logic-tree';

export interface IPublicationHistoryDialogProps {
	onClose: (event: any) => void;
	contentHeight?: boolean;
	project: EditorService;
}

const calculateMockPublishingDetails = (project: ProjectState) => {
	let deletedFunctionsCount = 0;
	let newFunctionsCount = 0;
	let modifiedFunctionsCount = 0;

	// So we can assume that every new entity results in 3 routes and 3 functions
	// +1 'options' method route for the http api which is always present
	const totalFunctionsCount = project.entities.length * 3 + 1;

	const costItems = [
		{
			value: 27,
			currency: 'EUR',
			valueType: 'absolute',
			type: 'cost',
			amount: 1,
			name: InfrastructureResourceType.RelationalDatabase,
		},
		{
			value: 0.5,
			currency: 'EUR',
			valueType: 'absolute',
			type: 'cost',
			amount: 1,
			name: InfrastructureResourceType.HttpApi,
		},
		{
			value: 0.5,
			currency: 'EUR',
			valueType: 'absolute',
			type: 'cost',
			amount: 1,
			name: InfrastructureResourceType.CloudHostedCodebase,
		},
		// If this is the first project published by the user, we add a discount of 28 EUR for the first month
		// 28 EUR should cover for a basic web server application with near-zero traffic
		...(true // Forcing true for now, to test
			? [
					{
						value: 28,
						currency: 'EUR',
						valueType: 'absolute',
						type: 'discount',
						amount: 1,
						name: 'first-published-project-monthly-discount',
					},
			  ]
			: []),
	];

	// Calculate the total price from the cost items
	const totalCostValue = costItems.reduce((total, costItem) => {
		if (costItem.type === 'discount') {
			return total - costItem.value;
		}
		return total + costItem.value;
	}, 0);

	const totalCostSummary = {
		value: totalCostValue,
		currency: 'EUR',
		valueType: 'absolute',
		type: totalCostValue >= 0 ? 'cost' : 'discount',
		amount: 1,
		name: 'total',
	};

	const resources = [
		{
			type: InfrastructureResourceType.RelationalDatabase,
			items: {
				total: {
					count: 1,
				},
				new: {
					count: true ? 1 : 0,
				},
				modified: {
					count: true ? 0 : 1,
				},
				deleted: {
					count: 0,
				},
			},
		},
		{
			type: InfrastructureResourceType.HttpEndpoint,
			items: {
				total: {
					count: totalFunctionsCount,
				},
				new: {
					count: true ? totalFunctionsCount : newFunctionsCount,
				},
				modified: {
					count: modifiedFunctionsCount,
				},
				deleted: {
					count: deletedFunctionsCount,
				},
			},
		},
		{
			type: InfrastructureResourceType.ServerlessFunction,
			items: {
				total: {
					count: totalFunctionsCount,
				},
				new: {
					count: true ? totalFunctionsCount : newFunctionsCount,
				},
				modified: {
					count: modifiedFunctionsCount,
				},
				deleted: {
					count: deletedFunctionsCount,
				},
			},
		},
	];

	const data = {
		resources,
		publicationSequence: AWS_SERVER_SIDE_PROJECT_DEPLOYMENT_SEQUENCE,
		monthlyCostForecast: {
			items: costItems,
			total: totalCostSummary,
		},
	};

	return data;
};

const PublicationHistoryDialog: React.FC<IPublicationHistoryDialogProps> = ({
	onClose,
	project,
}) => {
	if (!project || !project.logic || !project.ready) {
		return (
			<DialogLoader
				title='Project publication'
				subtitle='Publish your project to the cloud'
				onClose={onClose}
			/>
		);
	}

	const { id: projectId } = useParams<{ id: string }>();

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				padding: '40px',
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
				<button className='dialog_back_button' onClick={onClose}>
					<i className='fa-solid fa-arrow-left' />
				</button>
				<h1 style={{ margin: 0, fontWeight: 500, fontSize: '28px' }}>
					Project publication
				</h1>
			</div>
				<a
					style={{
						display: 'flex',
						gap: '6px',
						marginLeft: '42px',
						textDecoration: 'none',
					}}
					href=''
					onClick={(event) => {
						event.preventDefault();
						EditorService.navigate(`${project?.editorBasePath}/publication/preview`);
					}}
				>
					<i
						className='fa-solid fa-flag-checkered'
						style={{ color: 'rgba(0,0,0,0.5)' }}
					/>
					<p style={{ margin: 0, color: 'rgba(0,0,0,0.7)' }}>
						Publish latest version
					</p>
				</a>

			<div style={{ flex: 1, margin: '0 42px', marginTop: '30px' }}>
				<PublicationHistoryContent onClose={onClose} project={project} />
			</div>
			<div
				style={{
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px',
				}}
			>
				<Button
					onClick={() => {
						EditorService.navigate(`${project?.editorBasePath}/publication/preview`);
					}}
				>
					Back
				</Button>
			</div>
		</div>
	);
};

export default PublicationHistoryDialog;
